import { Cancel, CheckCircle, Close, Edit } from '@mui/icons-material';
import { Box, Button, Theme, Typography, useMediaQuery } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState, FC } from 'react';
import { Loader, Pagination, Table } from '../../../components';
import { deletePerson, getPerson, putPeopleActivation, resetTwoFactorAuth } from '../../../fetch';
import { IPeople, IPeopleDetail } from '../../../models';
import { AddEditExternalUser } from './ExternalUserModal';
import { MobileExternalUser } from './MobileExternalUser';
import { ExternalFilters } from './external-filters';
import { getCommonPeople } from '../../../fetch/lookups';

interface IExternalUsers {
  selectedUsersTab: number;
  isActive: boolean;
  personType?: string;
  personId?: string;
  isAddEditExternalUserModalOpen: boolean;
  setIsAddEditExternalUserModalOpen: (val: boolean) => void;
  setPage: (val: number) => void;
  page: number;
  isVendorAffiliate?: boolean;
}

export const ExternalUsers: FC<IExternalUsers> = ({
  selectedUsersTab,
  isActive,
  personType,
  isAddEditExternalUserModalOpen,
  setIsAddEditExternalUserModalOpen,
  page,
  setPage,
  personId,
  isVendorAffiliate
}) => {
  const classes = useStyles();
  const [isLoading, setLoading] = useState(false);
  const [externalUsers, setExternalUsers] = useState<IPeople[]>([]);
  const [personIdParam, setPersonIdParam] = useState<string | null>(personId ?? null);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const isMobile = useMediaQuery('(max-width: 1100px)');

  const [currentExternalUser, setCurrentExternalUser] = useState<IPeopleDetail>(null);
  const [loadingExternalUser, setLoadingExternalUser] = useState(false);
  const [searchValue, setSearch] = useState<string>('');
  const [searchedValue, setSearchedValue] = useState<string>('');

  const { enqueueSnackbar } = useSnackbar();

  const [selectedSort, setSelectedSort] = useState<string>('FirstName');
  const [sortDirection, setSortDirection] = useState<{
    FirstName?: 'Asc' | 'Desc';
    Email?: 'Asc' | 'Desc';
    LastName?: 'Asc' | 'Desc';
    Company?: 'Asc' | 'Desc';
    Type?: 'Asc' | 'Desc';
  }>({
    FirstName: 'Asc'
  });
  const [hasAppliedFilters, setHasAppliedFilters] = useState<boolean>(false);
  const [filters, setFilters] = useState<{
    search?: string;
    personType?: string | number;
    partnerId?: string | number;
  } | null>({});
  const [selectedPersonType, setSelectedPersonType] = useState<string | number>('');
  const [selectedPartnerId, setSelectedPartnerId] = useState<string | number>('');

  const handleExternalEdit = async (id: string, showLoading: boolean) => {
    if (showLoading) {
      setLoadingExternalUser(true);
    }
    try {
      const externalUser = await getPerson(id);
      setCurrentExternalUser(externalUser);
      setIsAddEditExternalUserModalOpen(true);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingExternalUser(false);
    }
  };

  const [deletingExternalUser, setDeletingExternalUser] = useState(false);
  const handleExternalDelete = async id => {
    setDeletingExternalUser(true);
    const result = window.confirm('Are you sure you want to deactivate this user?');
    if (result) {
      try {
        await deletePerson(id);

        enqueueSnackbar(`User Deactivated!`, {
          variant: 'success'
        });
        fetchExternalUsers();
      } catch (error) {
        console.log(error);
        enqueueSnackbar(`This User was unable to be deactivated, please try again.`, {
          variant: 'error'
        });
      }
    }
    setDeletingExternalUser(false);
  };

  const handleExternalActivation = async id => {
    setLoading(true);
    try {
      await putPeopleActivation(id, true);
      enqueueSnackbar(`User Activated!`, {
        variant: 'success'
      });
      fetchExternalUsers();
    } catch (error) {
      enqueueSnackbar(error?.Detail || `This User was unable to be activated, please try again.`, {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetAuth = async (original: any, showLoader?: boolean) => {
    const result = window.confirm('Are you sure you want to reset two factor auth for this user?');
    if (result) {
      try {
        await resetTwoFactorAuth(original.email);
        enqueueSnackbar('Two factor auth reset!', {
          variant: 'success'
        });
      } catch (error: any) {
        const variant =
          error?.Detail &&
          (error.Detail.includes('User not enrolled in MFA') ||
            error.Detail.includes('All user MFA enrollments inactive'))
            ? 'warning'
            : 'error';
        enqueueSnackbar(error?.Detail ?? 'User does not exist', {
          variant: variant
        });
      }
    }
  };

  const fetchExternalUsers = async (clearSearch?: boolean) => {
    setLoading(true);
    try {
      const extUsers = await getCommonPeople({
        page: page + 1,
        perPage: perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        query: clearSearch ? undefined : searchValue,
        personType: isVendorAffiliate ? 'VendorAffiliate' : personType || filters?.personType,
        partnerId: filters?.partnerId,
        isActive: isActive
      });
      setRecordCount(extUsers.totalRecordCount);
      setExternalUsers(extUsers.records);
      if (!clearSearch && searchValue) {
        setSearchedValue(searchValue);
      } else {
        setSearch('');
        setSearchedValue('');
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'firstName',
        isServerSorted: selectedSort === 'FirstName',
        isServerSortedDesc: sortDirection.FirstName === 'Desc',
        handleClickColumn: () => handleClickColumn('FirstName'),
        Cell: props => (
          <span>
            {props.cell.row.original && props.cell.row.original.firstName
              ? `${props.cell.row.original.firstName} ${props.cell.row.original.lastName}`
              : ''}
          </span>
        )
      },
      {
        Header: 'Email',
        accessor: 'email',
        isServerSorted: selectedSort === 'Email',
        isServerSortedDesc: sortDirection.Email === 'Desc',
        handleClickColumn: () => handleClickColumn('Email')
      },
      {
        Header: 'Company',
        accessor: 'company',
        isServerSorted: selectedSort === 'Company',
        isServerSortedDesc: sortDirection.Company === 'Desc',
        handleClickColumn: () => handleClickColumn('Company'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPeople } };
        }) => {
          return (
            <>
              {original.companies?.length > 0
                ? original.companies?.join(', ')
                : original.workplace ?? ''}
            </>
          );
        }
      },
      !isVendorAffiliate && {
        Header: 'Person Type',
        accessor: 'type',
        sort: !isVendorAffiliate,
        isServerSorted: selectedSort === 'Type',
        isServerSortedDesc: sortDirection.Type === 'Desc',
        handleClickColumn: () => handleClickColumn('Type'),
        Cell: props => (
          <span>{props.cell.row.original.partnerType ?? props.cell.row.original.type}</span>
        )
      },
      !isVendorAffiliate && {
        Header: 'Locations',
        accessor: 'locations',
        sort: false,
        overrideWidth: 300,
        Cell: props => (
          <div className={classes.truncate} title={props.cell.row.original.locations}>
            {props.cell.row.original.locations}
          </div>
        )
      },
      {
        id: 'actions',
        Header: '',
        accessor: '',
        overrideWidth: 250,
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IPeople } };
        }) => {
          return (
            <div className={classes.actionButtons}>
              <Button
                id="edit"
                color="primary"
                startIcon={<Edit />}
                className={classes.editButton}
                onClick={() => {
                  handleExternalEdit(`${original.personId}`, true);
                }}
              >
                Edit
              </Button>
              {original.isActive && (
                <Button
                  id="deactivate"
                  color="primary"
                  className={classes.deleteButton}
                  startIcon={<Cancel />}
                  onClick={() => {
                    handleExternalDelete(original.personId);
                  }}
                >
                  Deactivate
                </Button>
              )}
              {!original.isActive && (
                <Button
                  id="activate"
                  color="primary"
                  className={classes.activateButton}
                  startIcon={<CheckCircle />}
                  onClick={() => {
                    handleExternalActivation(original.personId);
                  }}
                >
                  Reactivate
                </Button>
              )}
            </div>
          );
        }
      }
    ].filter(Boolean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalUsers, isVendorAffiliate]);

  useEffect(() => {
    fetchExternalUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUsersTab, page, perPage, isActive, selectedSort, sortDirection, filters]);

  useEffect(() => {
    if (personIdParam) {
      handleExternalEdit(personIdParam, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personIdParam]);

  return (
    <>
      {(loadingExternalUser || deletingExternalUser) && (
        <Loader position="centered" type="fullscreen">
          Loading...
        </Loader>
      )}
      <Box mt={1}>
        <ExternalFilters
          isLoading={isLoading}
          setHasAppliedFilters={setHasAppliedFilters}
          hasAppliedFilters={hasAppliedFilters}
          setSearchValue={setSearch}
          setSearchedValue={setSearchedValue}
          searchValue={searchValue}
          setSelectedPersonType={setSelectedPersonType}
          selectedPersonType={selectedPersonType}
          setSelectedPartnerId={setSelectedPartnerId}
          selectedPartnerId={selectedPartnerId}
          isVendorAffiliate={isVendorAffiliate}
          handleSearch={(val: string) => {
            setPage(0);
            setSearchedValue(val);
            setFilters({
              ...filters,
              search: val
            });
          }}
          applyFilters={(clearFilters?: boolean, clearSearch?: boolean) => {
            if (clearFilters) {
              setFilters({
                search: '',
                personType: undefined,
                partnerId: undefined
              });
            } else {
              setPage(0);
              setFilters({
                ...filters,
                search: searchValue,
                personType: selectedPersonType || undefined,
                partnerId: selectedPartnerId || undefined
              });
            }
          }}
        />
      </Box>
      {searchedValue && (
        <Box marginTop="1rem" display="flex" alignItems="center">
          <Typography color="secondary">Showing results for "{searchedValue}"</Typography>
          <Button
            id="clear-search"
            size="small"
            startIcon={<Close />}
            className={classes.clearSearchButton}
            onClick={() => {
              setSearch('');
              setSearchedValue('');
              setPage(0);
              fetchExternalUsers(true);
            }}
          >
            Clear Search
          </Button>
        </Box>
      )}
      <Box marginTop="1rem">
        <Table
          columns={columns}
          data={externalUsers}
          isLoading={isLoading}
          hidePagination
          mobileBreakPoint={1100}
          ResponsiveComponent={isMobile ? MobileExternalUser : undefined}
          mobileProps={{
            handleExternalEdit: (val: IPeople) => handleExternalEdit(`${val.personId}`, true),
            handleExternalDelete: (val: string | number) => handleExternalDelete(val),
            handleExternalActivation: (val: string | number) => handleExternalActivation(val),
            handleResetAuth: (val: IPeople) => handleResetAuth(val, true),
            isVendorAffiliate
          }}
        />
        {!isLoading && (
          <Pagination
            page={page}
            count={recordCount}
            rowsPerPage={perPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        )}
      </Box>
      <AddEditExternalUser
        open={isAddEditExternalUserModalOpen}
        onClose={() => {
          setIsAddEditExternalUserModalOpen(false);
          setCurrentExternalUser(null);
          setPersonIdParam(null);
        }}
        currentExternalUser={currentExternalUser}
        handleExternalEdit={handleExternalEdit}
        fetchExternalUsers={fetchExternalUsers}
        isVendorAffiliate={isVendorAffiliate}
        modalTitle={
          isVendorAffiliate ? `${currentExternalUser ? 'Edit' : 'Add'} Vendor Affiliate` : ''
        }
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  actionButtons: {
    textAlign: 'right'
  },
  editButton: {
    textAlign: 'right'
  },
  deleteButton: {
    marginLeft: theme.spacing(0.5),
    color: theme.palette.error.main
  },
  activateButton: {
    marginLeft: theme.spacing(0.5)
  },
  clearSearchButton: {
    '@media (min-width: 408px)': {
      marginTop: 3,
      marginLeft: theme.spacing(1)
    }
  },
  truncate: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    width: '100%'
  }
}));
